import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Badge, ConfigProvider, Tooltip } from 'antd';
import {
  DashboardOutlined,
  FolderOutlined,
  FileTextOutlined,
  SettingOutlined,
  BellOutlined,
  CheckSquareOutlined,
  MenuFoldOutlined,
  SendOutlined,
  ProjectOutlined,
  TeamOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/auth/useAuth';
import { useActionItemStore } from '@/store/actionItemStore';
import '@/styles/sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: () => void;
}

const Sidebar = ({ collapsed, onCollapse }: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole } = useAuth();

  const isAdmin = hasRole('ADMIN');
  const isCeo = hasRole('CEO');
  const actionItemCount = useActionItemStore((s) => s.count);

  // Submenu open state — closed by default, auto-opens only when the current
  // route is under /admin, and stays freely toggleable by the user.
  const [openKeys, setOpenKeys] = useState<string[]>(
    location.pathname.startsWith('/admin') ? ['/admin'] : [],
  );

  useEffect(() => {
    if (location.pathname.startsWith('/admin')) {
      setOpenKeys((prev) => (prev.includes('/admin') ? prev : [...prev, '/admin']));
    }
  }, [location.pathname]);

  const adminChildren = [
    { key: '/admin/users', label: 'Users' },
    ...(isAdmin ? [
      { key: '/admin/raci', label: 'RACI Config' },
      { key: '/admin/settings', label: 'Settings' },
    ] : []),
  ];

  const adminMenuItem = {
    key: '/admin',
    icon: <SettingOutlined />,
    label: 'Admin',
    children: adminChildren,
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: '/action-items',
      icon: <CheckSquareOutlined />,
      label: (
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          My Action Items
          {actionItemCount > 0 && (
            <Badge count={actionItemCount} size="small" style={{ background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)', boxShadow: 'none' }} />
          )}
        </span>
      ),
    },
    {
      key: '/opportunities',
      icon: <FolderOutlined />,
      label: 'Opportunities',
    },
    ...((isAdmin || isCeo) ? [{
      key: '/task-board',
      icon: <ProjectOutlined />,
      label: 'Pipeline Board',
    }] : []),
    ...((isAdmin || hasRole('REQUIREMENT_RESOURCING_MGR')) ? [{
      key: '/talent-pool',
      icon: <TeamOutlined />,
      label: 'Talent Pool',
    }] : []),
    {
      key: '/discussions',
      icon: <MessageOutlined />,
      label: 'Discussions',
    },
    {
      key: '/content-library',
      icon: <FileTextOutlined />,
      label: 'Content Library',
    },
    // {
    //   key: '/notifications',
    //   icon: <BellOutlined />,
    //   label: 'Notifications',
    // },
    {
      key: '/post-submission',
      icon: <SendOutlined />,
      label: 'Post-Submission',
    },
    ...((isAdmin || isCeo) ? [adminMenuItem] : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  return (
    <div className="app-sidebar" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Logo + Collapse Toggle */}
      <div
        style={{
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          padding: collapsed ? '0' : '0 16px 0 20px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          gap: 12,
          flexShrink: 0,
        }}
      >
        {collapsed ? (
          <Tooltip title="Expand" placement="right">
            <div
              className="sidebar-logo-badge"
              onClick={onCollapse}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 15,
                fontWeight: 700,
                boxShadow: '0 6px 16px -4px rgba(37, 99, 235, 0.6)',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              R
            </div>
          </Tooltip>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'linear-gradient(135deg, #3B82F6, #1D4ED8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 700,
                  boxShadow: '0 6px 16px -4px rgba(37, 99, 235, 0.6)',
                  flexShrink: 0,
                }}
              >
                R
              </div>
              <span style={{ fontSize: 16, fontWeight: 700, color: '#F8FAFC', letterSpacing: '-0.01em' }}>
                RFO System
              </span>
            </div>
            <Tooltip title="Collapse" placement="right">
              <button
                type="button"
                className="sidebar-collapse-btn"
                onClick={onCollapse}
                aria-label="Collapse sidebar"
              >
                <MenuFoldOutlined />
              </button>
            </Tooltip>
          </>
        )}
      </div>

      {/* Navigation Menu */}
      <div style={{ flex: 1, padding: '8px 0', overflow: 'auto' }}>
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                itemHeight: 44,
                itemMarginBlock: 4,
                itemMarginInline: 8,
                itemPaddingInline: 12,
                itemBorderRadius: 10,
                iconSize: 16,
                collapsedIconSize: 18,
                iconMarginInlineEnd: 12,
                darkItemBg: 'transparent',
                darkSubMenuItemBg: 'transparent',
                darkItemColor: '#94A3B8',
                darkItemHoverBg: 'rgba(255, 255, 255, 0.06)',
                darkItemHoverColor: '#FFFFFF',
                darkItemSelectedBg: 'rgba(59, 130, 246, 0.18)',
                darkItemSelectedColor: '#FFFFFF',
              },
            },
          }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            openKeys={openKeys}
            onOpenChange={(keys) => setOpenKeys(keys as string[])}
            items={menuItems}
            onClick={handleMenuClick}
            theme="dark"
            style={{ border: 'none', background: 'transparent' }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default Sidebar;
